import * as React from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

// ─── Constants ────────────────────────────────────────────────────────────────

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const MINUTE_STEP = 5;
const MINUTES = Array.from({ length: 60 / MINUTE_STEP }, (_, index) => index * MINUTE_STEP);

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TimeOfDay {
  hour: number;
  minute: number;
}

export interface TimeOfDaySelectProps {
  onChange: (time: TimeOfDay) => void;
  time: TimeOfDay;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Zero-pads a number to 2 digits (e.g. `5` -> `'05'`). */
export function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

/** Formats a `TimeOfDay` as `HH:mm`. */
export function formatTimeOfDay(time: TimeOfDay): string {
  return `${pad2(time.hour)}:${pad2(time.minute)}`;
}

/** Extracts the hour/minute of a `Date` as a `TimeOfDay`. */
export function toTimeOfDay(date: Date): TimeOfDay {
  return { hour: date.getHours(), minute: date.getMinutes() };
}

/** Returns a new `Date` on the same day as `date`, with hour/minute set from `time` (seconds/ms zeroed). */
export function setHoursMinutes(date: Date, time: TimeOfDay): Date {
  const result = new Date(date);
  result.setHours(time.hour, time.minute, 0, 0);
  return result;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Paired hour/minute `Select` group rendered as a single bordered control. */
export function TimeOfDaySelect({ onChange, time }: TimeOfDaySelectProps): React.JSX.Element {
  const minuteOptions = buildMinuteOptions(time.minute);

  return (
    <div className="inline-flex items-center gap-0.5 rounded-md border bg-background px-2 py-1.5 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1">
      <Select onValueChange={(v): void => onChange({ ...time, hour: Number(v) })} value={String(time.hour)}>
        <SelectTrigger className="h-auto w-[46px] border-0 bg-transparent p-0 text-sm font-semibold tabular-nums shadow-none focus:ring-0 focus:ring-offset-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {HOURS.map((hour) => (
            <SelectItem key={hour} value={String(hour)}>
              {pad2(hour)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-sm font-semibold text-muted-foreground">:</span>
      <Select onValueChange={(v): void => onChange({ ...time, minute: Number(v) })} value={String(time.minute)}>
        <SelectTrigger className="h-auto w-[46px] border-0 bg-transparent p-0 text-sm font-semibold tabular-nums shadow-none focus:ring-0 focus:ring-offset-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {minuteOptions.map((minute) => (
            <SelectItem key={minute} value={String(minute)}>
              {pad2(minute)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ─── Helpers (private) ──────────────────────────────────────────────────────────

/**
 * The 5-minute step options, plus `selectedMinute` itself (sorted in) when it falls off that step —
 * an off-step value (e.g. `:03` seeded from "now") would otherwise match no option and the Radix
 * `SelectValue` would render an empty trigger instead of the real value.
 */
function buildMinuteOptions(selectedMinute: number): number[] {
  if (MINUTES.includes(selectedMinute)) return MINUTES;
  return [...MINUTES, selectedMinute].sort((a, b) => a - b);
}
